import { forwardRef, useRef, useEffect, useState, type ReactNode } from 'react'
import { cn } from '../utils/cn.ts'

export interface FloatingOverlayProps {
  children: ReactNode
  panel: ReactNode
  panelVisible?: boolean
  className?: string
}

export const FloatingOverlay = forwardRef<HTMLDivElement, FloatingOverlayProps>(
  ({ children, panel, panelVisible = true, className }, ref) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const [panelHeight, setPanelHeight] = useState(0)

    useEffect(() => {
      if (!panelRef.current) return
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setPanelHeight(entry.contentRect.height)
        }
      })
      observer.observe(panelRef.current)
      return () => observer.disconnect()
    }, [])

    return (
      <div ref={ref} className={cn('floating-overlay', className)}>
        <div className="floating-overlay-scroll">
          {children}
          {panelVisible && panelHeight > 0 && (
            <div style={{ height: panelHeight + 32, flexShrink: 0 }} />
          )}
        </div>
        <div
          className={cn(
            'floating-overlay-panel',
            panelVisible ? 'floating-overlay-panel-visible' : 'floating-overlay-panel-hidden',
          )}
          aria-hidden={!panelVisible || undefined}
        >
          <div ref={panelRef} className="floating-overlay-panel-inner">
            {panel}
          </div>
        </div>
      </div>
    )
  },
)
FloatingOverlay.displayName = 'FloatingOverlay'
